import React, { ChangeEvent, Fragment, useEffect, useMemo, useState } from 'react';
// i18next
import { useTranslation } from 'react-i18next';
// Hooks
import useToggle from 'hooks/useToggle';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { deleteMedia, getMedia } from 'store/media/mediaAsync';
import { mediaActions } from 'store/media/mediaSlice';
import { selectQuiz } from 'store/quiz/quizzesSelectors';
import { selectMedia, selectSelectedMedia } from 'store/media/mediaSelectors';
import IMedia from 'models/Media';
import MediaFormats from 'types/MediaFormats';
// Icons
import { FaTrash, FaEye } from 'react-icons/fa';
// Layouts
import { Dialog, DialogTitle, DialogContent, DialogActions } from 'layouts/Dialog';
// Components
import { Button, IconButton } from 'components/Buttons';
import { Select } from 'components/Controls';
import { Loader } from 'components/Utilities';
import MediaPreviewDialog from './MediaPreview.dialog';
import ConfirmationDialog from './Confirmation.dialog';
import UploadImageDialog from './UploadImage.dialog';
import UploadVideoDialog from './UploadVideo.dialog';
// Utilities
import { textFromCamelToNormalCase } from 'utilities/utilities';

type Props = {
  open: boolean;
  onClose: () => void;
  type: 'image' | 'video';
  onChange: (mediaUrl: string) => void;
  initialFormat?: MediaFormats;
}

const MediaDialog:React.FC<Props> = ({ open, onClose, type, onChange, initialFormat = MediaFormats.Logo }) => {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const quiz = useAppSelector(selectQuiz);
  const media = useAppSelector(selectMedia);
  const [format, setFormat] = useState<MediaFormats | string>(initialFormat);

  const selectedMedia = useAppSelector(selectSelectedMedia);

  const handleFormat = (e:ChangeEvent<HTMLSelectElement>) => {
    setFormat(e.target.value);
  }

  const filteredMedia = useMemo(() => {
    return media.filter(media => {
      const formatFilter = format === 'all' || type === 'video' ? true : media.format === format;
      const typeFilter = media.type === type;
      return formatFilter && typeFilter;
    });
  }, [media, format, type]);

  const { open:openPreview, toggle:togglePreview } = useToggle();
  const { open:openDeleteDialog, toggle:toggleDeleteDialog } = useToggle();

  const onClick = (media: IMedia) => {
    dispatch(mediaActions.setSelectedMedia(media));
  }

  const handleDelete = () => {
    if (!selectedMedia) return;
    dispatch(deleteMedia(selectedMedia._id));
  }

  const handleClickApply = () => {
    if (!selectedMedia) return;
    onChange('');
    setTimeout(() => onChange(selectedMedia.href), 0);
    onClose();
  }

  useEffect(() => {
    setIsLoading(true);
    let params:any = { scope: ['account'] };
    if (quiz) params = { scope: ['account', 'quiz'], quizId: quiz._id };
    dispatch(getMedia(params)).unwrap()
      .finally(() => setIsLoading(false));

    return () => {
      dispatch(mediaActions.setInitialField('media'));
      dispatch(mediaActions.setInitialField('selectedMedia'));
    }
    // eslint-disable-next-line
  }, []);

  const { open:openImageUploadDialog, toggle:toggleImageUploadDialog } = useToggle();
  const { open:openVideoUploadDialog, toggle:toggleVideoUploadDialog } = useToggle();

  const openUploadDialog = () => {
    if (type === 'image') toggleImageUploadDialog();
    if (type === 'video') toggleVideoUploadDialog();
  }

  const isDisabled = !selectedMedia;

  return (
    <Fragment>
      {openImageUploadDialog ? (
        <UploadImageDialog
          open={openImageUploadDialog}
          onClose={toggleImageUploadDialog}
          initialFormat={initialFormat}
        />
      ) : null}

      {openVideoUploadDialog ? (
        <UploadVideoDialog
          open={openVideoUploadDialog}
          onClose={toggleVideoUploadDialog}
        />
      ) : null}

      {openPreview ? (
        <MediaPreviewDialog open={openPreview} onClose={togglePreview} media={selectedMedia} />
      ) : null}

      {openDeleteDialog ? (
        <ConfirmationDialog
          open={openDeleteDialog}
          onClose={toggleDeleteDialog}
          title="Delete media"
          content="Are you sure you want to delete media?"
          loading={false}
          onConfirm={handleDelete}
        />
      ) : null}

      <Dialog size="xl" open={open} onClose={onClose}>
        <DialogTitle title="Media gallery" onClose={onClose} />
        <DialogContent>
          {isLoading && <Loader />}
          {type === 'image' && (
            <Select
              id="media-format" name="media-format"
              options={formatOptions}
              value={format}
              onChange={handleFormat}
              label="Format"
            />
          )}
          <div className="flex flex-wrap gap-4 mb-2">
            {filteredMedia.map(m => (
              <div
                key={m._id}
                onClick={() => onClick(m)}
                className="w-56 h-32 relative border"
                style={{
                  boxShadow: selectedMedia?._id === m._id ? '0 10px 15px -3px rgba(255, 255, 255, 0.2), 0 4px 6px -4px rgba(255, 255, 255, 0.4)' : 'none',
                  borderColor: selectedMedia?._id === m._id ? '#ff9000' : 'inherit',
                }}
              >
                {m.type === 'image' ? (
                  <div className="w-full h-full" style={{
                    background: `url('${m.href}') no-repeat center / ${format === 'all' ? 'cover' : 'contain' }`,
                    cursor: 'pointer'
                  }} />
                ) : (
                  <video className="w-full h-full object-center object-cover" controls>
                    <source src={m.href} type="video/mp4" />
                  </video>
                )}
                <div className="absolute top-0 right-0 bg-gray-800 rounded">
                  <IconButton size="sm" onClick={togglePreview}>
                    <FaEye />
                  </IconButton>
                  <IconButton size="sm" onClick={toggleDeleteDialog} className="hover:text-red-600">
                    <FaTrash />
                  </IconButton>
                </div>
              </div>
            ))}
          </div>
          <span className="block text-gray-400 text-sm font-light leading-5 mb-2">Select media file</span>
          <Button
            onClick={openUploadDialog}
            variant="outlined"
          >Upload new</Button>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onClose}
          >{t('common.cancel')}</Button>
          <Button
            className="ml-2"
            variant="contained"
            disabled={isDisabled}
            onClick={handleClickApply}
          >{t('common.apply')}</Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  )
}

export default MediaDialog;

const formatOptions = [
  { value: 'all', label: 'All' },
  ...(Object.keys(MediaFormats) as Array<keyof typeof MediaFormats>)
  .map((key) => ({ value: MediaFormats[key], label: textFromCamelToNormalCase(key) }))
];
